<template>
  <div>
    <HeadingSection
      class="!mt-0 mb-[35px]"
      heading="Container Types"
      desc="The container types this account groups its trackers by."
      :button="topButton"
    />
    <DataTable
      v-if="!isEmpty"
      :loading="isLoading"
      :list="rows"
      :columns="columns"
      :edit-actions="editActions"
    />
    <DataTableNav v-if="!isEmpty" v-model="pagination" :meta="paginationMeta" />
    <EmptyState
      v-if="isEmpty"
      class="mt-[35px]"
      type="table"
      heading="There are no container types yet"
      desc="Add one, then set it on each tracker from the tracker's own page."
    />
  </div>
</template>

<script setup>
import { computed, ref } from 'vue';
import { useStore } from 'vuex';
import {
  getAccountContainerTypes,
  deleteAccountContainerType,
} from '@gpx-spa/queries';
import DataTable from '@gpx-spa/ui/src/elements/DataTable.vue';
import DataTableNav from '@gpx-spa/ui/src/elements/DataTableNav.vue';
import EmptyState from '@gpx-spa/ui/src/components/EmptyState.vue';
import { TableResultsPerPage } from '@gpx-spa/constants';
import HeadingSection from '@/components/_common/Heading/HeadingSection.vue';
import { formatTime } from '@/filters';
import { formatContainerTypeValue } from '@/utils/container-type-value';

const props = defineProps({
  accountId: {
    type: Number,
    required: true,
  },
});

const store = useStore();

const pagination = ref({ page: 1, per_page: TableResultsPerPage[0] });

const { data, isLoading } = getAccountContainerTypes(
  computed(() => props.accountId),
  pagination,
);
const deleteMutation = deleteAccountContainerType();

const types = computed(() => data.value?.data || []);

const columns = ['Name', 'Value', 'Trackers', 'Created'];

const rows = computed(() =>
  types.value.map((type) => ({
    id: type.id,
    name: type.name,
    value: formatContainerTypeValue(type.value),
    trackers: type.device_count ?? 0,
    created: formatTime(type.created_at, 'MMM d, yyyy'),
    edit: true,
  })),
);

const isEmpty = computed(() => rows.value.length === 0 && !isLoading.value);

const paginationMeta = computed(
  () =>
    data.value?.meta || {
      current_page: pagination.value.page,
      last_page: 1,
      per_page: pagination.value.per_page,
      from: 0,
      to: 0,
      total: 0,
    },
);

const topButton = computed(() => ({
  label: 'Add container type',
  action: { mode: 'callback', callback: openCreate },
}));

const openCreate = () => {
  store.dispatch('modal/open', { name: 'ManageContainerTypeModal' });
};

const openEdit = (id) => {
  const type = types.value.find((item) => item.id === id);
  if (!type) return;

  store.dispatch('modal/open', {
    name: 'ManageContainerTypeModal',
    params: { id: type.id, name: type.name, value: type.value },
  });
};

// The engine allows this even while trackers carry the type, so the count is the warning.
const openDelete = (id) => {
  const type = types.value.find((item) => item.id === id);
  if (!type) return;

  const count = type.device_count ?? 0;
  const body = count
    ? count === 1
      ? 'There is 1 tracker using this container type. Deleting it will remove the container type from that tracker.'
      : `There are ${count} trackers using this container type. Deleting it will remove the container type from those trackers.`
    : 'No tracker is using this container type. It can be added again at any time.';

  store.dispatch('modal/open', {
    name: 'CommonConfirmationModal',
    params: {
      actionLabel: 'Delete container type',
      title: type.name,
      body,
      successMessage: 'Container type deleted',
      errorMessage: 'Failed to delete the container type.',
      onOk: () =>
        deleteMutation.mutateAsync({
          accountId: props.accountId,
          id: type.id,
        }),
    },
  });
};

const editActions = [
  {
    icon: 'PencilSquareIcon',
    label: 'Edit',
    action: { mode: 'fn', fn: openEdit },
  },
  {
    icon: 'XMarkIcon',
    label: 'Delete',
    action: { mode: 'fn', fn: openDelete },
  },
];
</script>
